import { createSlice } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';

/**
 * Spreadsheet-style cell selection state.
 *
 * Selection is stored as a list of rectangular `ranges`. Each range is defined
 * by an `anchor` (where the gesture started) and a `focus` (where it currently
 * ends); the selected cells are the union of every range's bounding box. The
 * last range in the list is the "active" range — the one that drag and
 * shift-click extend.
 *
 * @property ranges - The rectangular ranges that make up the current selection.
 * @property isDragging - True while a mouse drag is in progress, so mouseenter
 *   on a cell extends the active range.
 */

const initialState = {
  ranges: [],
  isDragging: false
};
const isSameCoord = (a, b) => a.rowIndex === b.rowIndex && a.columnIndex === b.columnIndex;
const setActiveFocus = (state, coord) => {
  const activeRange = state.ranges[state.ranges.length - 1];
  // Skip the write when the focus is already this cell. Auto-scroll dispatches
  // dragOver every animation frame while the pointer is held at an edge; without
  // this guard each identical dispatch produces a new state object and forces
  // selection selectors to recompute/re-render for no visible change.
  if (activeRange && !isSameCoord(activeRange.focus, coord)) {
    activeRange.focus = coord;
  }
};

// True when the entire selection is exactly the single cell at `coord`.
const isOnlySelectedCell = (state, coord) => {
  if (state.ranges.length !== 1) return false;
  const [range] = state.ranges;
  return isSameCoord(range.anchor, coord) && isSameCoord(range.focus, coord);
};
const rangeContains = (range, rowIndex, columnIndex) => {
  const minRow = Math.min(range.anchor.rowIndex, range.focus.rowIndex);
  const maxRow = Math.max(range.anchor.rowIndex, range.focus.rowIndex);
  const minCol = Math.min(range.anchor.columnIndex, range.focus.columnIndex);
  const maxCol = Math.max(range.anchor.columnIndex, range.focus.columnIndex);
  return rowIndex >= minRow && rowIndex <= maxRow && columnIndex >= minCol && columnIndex <= maxCol;
};
const isCellSelected = (ranges, coord) => ranges.some(range => rangeContains(range, coord.rowIndex, coord.columnIndex));

// Partition `range` minus the single cell at `coord` into up to four disjoint
// sub-rectangles (a full-width band above the cell, a full-width band below it,
// and the left/right remainders of the cell's own row). The union of the
// returned ranges is exactly `range` with `coord` removed. Assumes `coord` is
// inside `range`; a 1x1 range collapses to no ranges.
const splitRangeExcludingCell = (range, coord) => {
  const minRow = Math.min(range.anchor.rowIndex, range.focus.rowIndex);
  const maxRow = Math.max(range.anchor.rowIndex, range.focus.rowIndex);
  const minCol = Math.min(range.anchor.columnIndex, range.focus.columnIndex);
  const maxCol = Math.max(range.anchor.columnIndex, range.focus.columnIndex);
  const {
    rowIndex,
    columnIndex
  } = coord;
  const result = [];
  if (rowIndex > minRow) {
    result.push({
      anchor: {
        rowIndex: minRow,
        columnIndex: minCol
      },
      focus: {
        rowIndex: rowIndex - 1,
        columnIndex: maxCol
      }
    });
  }
  if (rowIndex < maxRow) {
    result.push({
      anchor: {
        rowIndex: rowIndex + 1,
        columnIndex: minCol
      },
      focus: {
        rowIndex: maxRow,
        columnIndex: maxCol
      }
    });
  }
  if (columnIndex > minCol) {
    result.push({
      anchor: {
        rowIndex,
        columnIndex: minCol
      },
      focus: {
        rowIndex,
        columnIndex: columnIndex - 1
      }
    });
  }
  if (columnIndex < maxCol) {
    result.push({
      anchor: {
        rowIndex,
        columnIndex: columnIndex + 1
      },
      focus: {
        rowIndex,
        columnIndex: maxCol
      }
    });
  }
  return result;
};
const cellSelectionSlice = createSlice({
  name: 'cellSelection',
  initialState,
  reducers: {
    // Plain mousedown: replace the selection with a single new range. If the
    // clicked cell is already the only selected cell, toggle it off instead.
    selectCell: (state, action) => {
      if (isOnlySelectedCell(state, action.payload)) {
        state.ranges = [];
        state.isDragging = false;
        return;
      }
      state.ranges = [{
        anchor: action.payload,
        focus: action.payload
      }];
      state.isDragging = true;
    },
    // Cmd/Ctrl mousedown: toggle the clicked cell. If it is already selected,
    // deselect it by splitting every range that contains it into sub-rectangles
    // covering the same cells minus this one (Google Sheets behaviour).
    // Otherwise begin a new, disjoint range without clearing the rest.
    addRange: (state, action) => {
      if (isCellSelected(state.ranges, action.payload)) {
        state.ranges = state.ranges.flatMap(range => rangeContains(range, action.payload.rowIndex, action.payload.columnIndex) ? splitRangeExcludingCell(range, action.payload) : [range]);
        state.isDragging = false;
        return;
      }
      state.ranges.push({
        anchor: action.payload,
        focus: action.payload
      });
      state.isDragging = true;
    },
    // Shift mousedown: extend the active range's focus to the clicked cell. With
    // no existing range, behaves like starting a fresh selection.
    extendActiveRange: (state, action) => {
      if (state.ranges.length === 0) {
        state.ranges = [{
          anchor: action.payload,
          focus: action.payload
        }];
        return;
      }
      setActiveFocus(state, action.payload);
    },
    // Mouseenter during a drag: extend the active range. No-op when not dragging.
    dragOver: (state, action) => {
      if (!state.isDragging) return;
      setActiveFocus(state, action.payload);
    },
    // Mousedown on the fill handle: begin a drag without altering the ranges, so
    // the subsequent mouseenters (dragOver) extend the active range from the
    // current selection.
    startFillDrag: state => {
      state.isDragging = true;
    },
    // Mouseup anywhere
    endDrag: state => {
      state.isDragging = false;
    },
    // Esc or click outside
    clearCellSelection: state => {
      state.ranges = [];
      state.isDragging = false;
    }
  }
});
export const cellSelectionReducer = cellSelectionSlice.reducer;
export const {
  selectCell,
  addRange,
  extendActiveRange,
  dragOver,
  startFillDrag,
  endDrag,
  clearCellSelection
} = cellSelectionSlice.actions;
const getCellSelectionState = ({
  cellSelection
}) => cellSelection;
const getRanges = createSelector([getCellSelectionState], ({
  ranges
}) => ranges);
export const getIsDragging = createSelector([getCellSelectionState], ({
  isDragging
}) => isDragging);

// True when any cells are selected. Drives whether the floating action menu
// should be shown at all.
export const getHasSelection = createSelector([getRanges], ranges => ranges.length > 0);

// The bottom-right corner of the active (last) range — the cell that carries
// the fill handle. Null when nothing is selected. The floating action menu
// anchors to this cell.
export const getActiveRangeBottomRightCoord = createSelector([getRanges], ranges => {
  if (ranges.length === 0) return null;
  const active = ranges[ranges.length - 1];
  return {
    rowIndex: Math.max(active.anchor.rowIndex, active.focus.rowIndex),
    columnIndex: Math.max(active.anchor.columnIndex, active.focus.columnIndex)
  };
});

// Per-cell selector: returns which outer edges of the selection region this
// cell sits on, as a substring of "trbl". Returns "" for interior cells (in a
// range but not on any edge) and null for unselected cells.
//
// The CSS side drives visibility via custom properties toggled by `*=`
// substring selectors, so "" vs null is a meaningful distinction: "" means
// the cell IS selected (and should carry user-select:none) but shows no border.
export const makeGetCellSelectionEdges = (rowIndex, columnIndex) => createSelector([getRanges], ranges => {
  let t = false;
  let r = false;
  let b = false;
  let l = false;
  let isSelected = false;
  for (const range of ranges) {
    if (!rangeContains(range, rowIndex, columnIndex)) continue;
    isSelected = true;
    const minRow = Math.min(range.anchor.rowIndex, range.focus.rowIndex);
    const maxRow = Math.max(range.anchor.rowIndex, range.focus.rowIndex);
    const minCol = Math.min(range.anchor.columnIndex, range.focus.columnIndex);
    const maxCol = Math.max(range.anchor.columnIndex, range.focus.columnIndex);
    if (rowIndex === minRow) t = true;
    if (columnIndex === maxCol) r = true;
    if (rowIndex === maxRow) b = true;
    if (columnIndex === minCol) l = true;
  }
  if (!isSelected) return null;
  return (t ? 't' : '') + (r ? 'r' : '') + (b ? 'b' : '') + (l ? 'l' : '');
});

// Per-cell selector: true when the cell is the bottom-right corner of the
// active (last) range, i.e. where the fill handle is drawn.
export const makeGetIsFillHandleCell = (rowIndex, columnIndex) => createSelector([getRanges], ranges => {
  if (ranges.length === 0) return false;
  const active = ranges[ranges.length - 1];
  const maxRow = Math.max(active.anchor.rowIndex, active.focus.rowIndex);
  const maxCol = Math.max(active.anchor.columnIndex, active.focus.columnIndex);
  return rowIndex === maxRow && columnIndex === maxCol;
});

// Flattened, de-duplicated list of every selected cell coordinate, ordered by
// range then row then column. Used to compute the selection-change callback.
export const getSelectedCellCoords = createSelector([getRanges], ranges => {
  const seen = new Set();
  const coords = [];
  ranges.forEach(range => {
    const minRow = Math.min(range.anchor.rowIndex, range.focus.rowIndex);
    const maxRow = Math.max(range.anchor.rowIndex, range.focus.rowIndex);
    const minCol = Math.min(range.anchor.columnIndex, range.focus.columnIndex);
    const maxCol = Math.max(range.anchor.columnIndex, range.focus.columnIndex);
    for (let rowIndex = minRow; rowIndex <= maxRow; rowIndex++) {
      for (let columnIndex = minCol; columnIndex <= maxCol; columnIndex++) {
        const key = `${rowIndex}:${columnIndex}`;
        if (!seen.has(key)) {
          seen.add(key);
          coords.push({
            rowIndex,
            columnIndex
          });
        }
      }
    }
  });
  return coords;
});